// Vaccine tech review — split-screen for Community Pharmacy vaccine claims.
// Left: consent form image via SAS URL.
// Right: editable form. Save persists via PATCH /rx/{id}/vaccine, Approve
// advances status to 'ready_for_pk' so the PioneerRx PAD picks it up.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiPatch, apiPost } from "../lib/api";
import type { RxDetail, VaccineDetail, InsuranceBranch, VaccineCode, AdminSite, TargetPms } from "@shared/types";
import { PART_B_ELIGIBLE_VACCINES, VACCINE_LABEL, TARGET_PMS_LABEL, allowedPmsForStore } from "@shared/types";

const VACCINE_OPTIONS: VaccineCode[] = [
  "fluzonehd","fluad","fluzone","spike","pfi","mnex","prevnar","boostrix","shingrix","rsv",
];

const INSURANCE_BRANCHES: { value: InsuranceBranch; label: string; hint: string }[] = [
  { value: "advantage",  label: "Advantage plan (Part D)",       hint: "Card is a Medicare Advantage plan → run through Part D." },
  { value: "partb_card", label: "Part B pharmacy card",          hint: "Card has BIN/PCN pharmacy processing info → run that card." },
  { value: "fdsmedical", label: "FDSMEDICAL (Part B via FLUTX)", hint: "No Advantage / no Part B card → BIN 007895 PCN FLUTX." },
  { value: "tricare",    label: "Tricare pharmacy benefits",     hint: "Tricare goes through Tricare, not Part B." },
  { value: "railroad",   label: "Railroad Medicare",             hint: "Same as Part B but with RR prefix on Cardholder ID." },
];

type Props = { rx: RxDetail; onRefetch: () => void };

export function VaccineReview({ rx, onRefetch }: Props) {
  const navigate = useNavigate();

  if (!rx.vaccine) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <p className="text-bad">Vaccine detail row is missing for rx #{rx.rxId}. This shouldn't happen — extraction should have created one.</p>
        <Link to="/" className="text-accent underline">Back to queue</Link>
      </div>
    );
  }

  const [targetPms, setTargetPms] = useState<TargetPms>(rx.targetPms);
  const [form, setForm] = useState<VaccineDetail>(rx.vaccine);
  const allowed = allowedPmsForStore(rx.store.code);
  const [patient, setPatient] = useState({
    nameRaw:  rx.patient.nameRaw ?? "",
    dobRaw:   toDateInput(rx.patient.dobRaw),
    phoneRaw: rx.patient.phoneRaw ?? "",
    street:   rx.patient.street   ?? "",
    city:     rx.patient.city     ?? "",
    state:    rx.patient.state    ?? "",
    zip:      rx.patient.zip      ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setForm(rx.vaccine!);
    setPatient({
      nameRaw:  rx.patient.nameRaw ?? "",
      dobRaw:   toDateInput(rx.patient.dobRaw),
      phoneRaw: rx.patient.phoneRaw ?? "",
      street:   rx.patient.street   ?? "",
      city:     rx.patient.city     ?? "",
      state:    rx.patient.state    ?? "",
      zip:      rx.patient.zip      ?? "",
    });
  }, [rx.vaccine?.rxId]);

  function set<K extends keyof VaccineDetail>(k: K, v: VaccineDetail[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  function setP<K extends keyof typeof patient>(k: K, v: string) {
    setPatient((p) => ({ ...p, [k]: v }));
  }

  const partBBranch = form.insuranceBranch === "partb_card"
                    || form.insuranceBranch === "fdsmedical"
                    || form.insuranceBranch === "railroad";
  const partBEligible = PART_B_ELIGIBLE_VACCINES.includes(form.vaccineCode);
  const partBViolation = partBBranch && !partBEligible;

  const admitBeforeVax = !form.facilityAdmittedOn
                      || !form.vaccinationDate
                      || form.facilityAdmittedOn <= form.vaccinationDate;

  // partb_card and tricare need the card details before PAD can proceed autonomously.
  const needsCardDetails = form.insuranceBranch === "partb_card" || form.insuranceBranch === "tricare";
  const hasCardDetails = !!form.cardBin && !!form.cardCardholderId;
  const cardViolation = needsCardDetails && !hasCardDetails;

  const canApprove = !partBViolation && admitBeforeVax && !cardViolation
    && !!form.medicareNumber && !!form.ssnLast4
    && !!form.vaccinationDate;

  async function save() {
    setSaving(true); setError(null);
    try {
      // Patient fields + target_pms live on dbo.rx — hit the generic rx PATCH.
      await apiPatch(`/rx/${rx.rxId}`, {
        target_pms:          targetPms,
        patient_name_raw:    patient.nameRaw    || null,
        patient_dob_raw:     patient.dobRaw     || null,
        patient_phone_raw:   patient.phoneRaw   || null,
        patient_street:      patient.street     || null,
        patient_city:        patient.city       || null,
        patient_state:       patient.state      || null,
        patient_zip:         patient.zip        || null,
        // keep the combined address_raw in sync for anything still reading it
        patient_address_raw: [patient.street, patient.city, patient.state, patient.zip].filter(Boolean).join(", ") || null,
      });
      // Vaccine fields live on dbo.rx_vaccine_details.
      await apiPatch(`/rx/${rx.rxId}/vaccine`, {
        cardBin:                     form.cardBin,
        cardPcn:                     form.cardPcn,
        cardCardholderId:            form.cardCardholderId,
        cardGroupNumber:             form.cardGroupNumber,
        vaccineCode:              form.vaccineCode,
        templateShortcut:         form.templateShortcut,
        lotNumber:                form.lotNumber,
        adminSite:                form.adminSite,
        vaccinationDate:          form.vaccinationDate,
        visDate:                  form.visDate,
        facilityWing:             form.facilityWing,
        facilityAdmittedOn:       form.facilityAdmittedOn,
        medicareNumber:           form.medicareNumber,
        ssnLast4:                 form.ssnLast4,
        insuranceBranch:          form.insuranceBranch,
        fdsmedicalStartDate:      form.fdsmedicalStartDate,
        railroadCardholderId:     form.railroadCardholderId,
        consentFormSignedDate:    form.consentFormSignedDate,
        consentFormSignaturePresent: form.consentFormSignaturePresent,
      });
      onRefetch();
    } catch (e) {
      setError(String(e));
    } finally { setSaving(false); }
  }

  async function approve() {
    if (!canApprove) return;
    setSaving(true); setError(null);
    try {
      await save();
      await apiPost(`/rx/${rx.rxId}/approve`, {});
      navigate("/");
    } catch (e) {
      setError(String(e));
    } finally { setSaving(false); }
  }

  async function reject() {
    const reason = window.prompt("Reject reason?");
    if (!reason) return;
    setSaving(true);
    try {
      await apiPost(`/rx/${rx.rxId}/reject`, { reason });
      navigate("/");
    } catch (e) {
      setError(String(e));
    } finally { setSaving(false); }
  }

  return (
    <div className="grid grid-cols-[1fr_520px] gap-4 h-[calc(100vh-64px)] p-4">
      {/* Left: consent form image */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden flex flex-col">
        <div className="px-3 py-2 border-b border-border text-xs uppercase tracking-wide text-ink-soft flex justify-between">
          <span>Consent form — {rx.intake.originalFilename ?? "scan"}</span>
          <span>Confidence {rx.rxConfidence?.toFixed(2) ?? "—"}</span>
        </div>
        <div className="flex-1 overflow-auto bg-[#f7f6f2]">
          {rx.intake.documentUrl
            ? <img src={rx.intake.documentUrl} alt="Consent form" className="w-full" />
            : <p className="p-6 text-ink-soft text-sm text-center">No document preview available.</p>}
        </div>
      </div>

      {/* Right: form */}
      <div className="bg-surface border border-border rounded-lg overflow-auto">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Vaccine claim — rx #{rx.rxId}</h2>
            <StatusPill status={rx.status} />
          </div>
          <p className="text-ink-soft text-sm mt-1">{VACCINE_LABEL[form.vaccineCode]}</p>
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="text-ink-soft">Send to:</span>
            <select value={targetPms} onChange={(e) => setTargetPms(e.target.value as TargetPms)} className="input">
              {allowed.map((p) => <option key={p} value={p}>{TARGET_PMS_LABEL[p]}</option>)}
            </select>
            {targetPms !== rx.targetPms && (
              <span className="text-warn text-xs">changed from {TARGET_PMS_LABEL[rx.targetPms]}</span>
            )}
          </div>
          {rx.warnings.length > 0 && (
            <ul className="mt-2 text-warn text-xs list-disc list-inside">
              {rx.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
        </div>

        {/* Patient */}
        <Section title="Patient">
          <Row label="Name">
            <input value={patient.nameRaw} onChange={(e) => setP("nameRaw", e.target.value)} className="input" />
          </Row>
          <Row label="DOB">
            <input type="date" value={patient.dobRaw} onChange={(e) => setP("dobRaw", e.target.value)} className="input" />
            {patient.dobRaw && <span className="ml-2 text-ink-soft text-xs">({formatMDY(patient.dobRaw)}, age {ageFrom(patient.dobRaw)})</span>}
          </Row>
          <Row label="Phone">
            <input value={patient.phoneRaw} onChange={(e) => setP("phoneRaw", e.target.value)} className="input" />
          </Row>
          <Row label="Street">
            <input value={patient.street} onChange={(e) => setP("street", e.target.value)} className="input" />
          </Row>
          <Row label="City">
            <input value={patient.city} onChange={(e) => setP("city", e.target.value)} className="input" />
          </Row>
          <Row label="State">
            <input value={patient.state} maxLength={2} onChange={(e) => setP("state", e.target.value.toUpperCase())} className="input w-16" />
          </Row>
          <Row label="ZIP">
            <input value={patient.zip} maxLength={10} onChange={(e) => setP("zip", e.target.value)} className="input w-28" />
          </Row>
        </Section>

        {/* Vaccine */}
        <Section title="Vaccine">
          <Row label="Product">
            <select value={form.vaccineCode}
              onChange={(e) => {
                const code = e.target.value as VaccineCode;
                const shortcut = TEMPLATE_SHORTCUT[code];
                set("vaccineCode", code);
                set("templateShortcut", shortcut);
              }}
              className="input">
              {VACCINE_OPTIONS.map((c) => <option key={c} value={c}>{VACCINE_LABEL[c]}</option>)}
            </select>
          </Row>
          <Row label="Lot #">
            <input value={form.lotNumber ?? ""} onChange={(e) => set("lotNumber", e.target.value || null)} className="input" />
          </Row>
          <Row label="Admin site">
            <select value={form.adminSite ?? ""} onChange={(e) => set("adminSite", (e.target.value || null) as AdminSite | null)} className="input">
              <option value="">—</option>
              <option value="LD">Left Deltoid</option>
              <option value="RD">Right Deltoid</option>
            </select>
          </Row>
          <Row label="Vaccination date">
            <input type="date" value={form.vaccinationDate ?? ""} onChange={(e) => set("vaccinationDate", e.target.value)} className="input" />
          </Row>
          <Row label="VIS date">
            <input type="date" value={form.visDate ?? ""} onChange={(e) => set("visDate", e.target.value || null)} className="input" />
          </Row>
          <Row label="Facility wing">
            <input value={form.facilityWing ?? ""} onChange={(e) => set("facilityWing", e.target.value || null)} placeholder="e.g., Robson Ranch" className="input" />
          </Row>
          <Row label="Admitted on">
            <input type="date" value={form.facilityAdmittedOn ?? ""} onChange={(e) => set("facilityAdmittedOn", e.target.value || null)} className="input" />
          </Row>
          {!admitBeforeVax && (
            <p className="text-bad text-xs px-3">Admitted-on date must be on/before the vaccination date (typing tip #1).</p>
          )}
        </Section>

        {/* Medicare */}
        <Section title="Medicare (verify against red-white-blue card)">
          <Row label="Medicare / MBI #">
            <input value={form.medicareNumber ?? ""} onChange={(e) => set("medicareNumber", e.target.value.toUpperCase() || null)} className="input font-mono" />
          </Row>
          <Row label="Last 4 SSN">
            <input value={form.ssnLast4 ?? ""} maxLength={4} onChange={(e) => set("ssnLast4", e.target.value.replace(/\D/g, "").slice(0, 4) || null)} className="input font-mono w-24" />
          </Row>
          <p className="text-ink-soft text-xs px-3">
            Name in PioneerRx must match the red-white-blue card exactly or the claim will deny (typing tip #11).
          </p>
        </Section>

        {/* Insurance branch */}
        <Section title="Insurance branch">
          {INSURANCE_BRANCHES.map((b) => (
            <label key={b.value} className="flex gap-2 items-start px-3 py-1.5 cursor-pointer hover:bg-pill">
              <input type="radio" name="branch" checked={form.insuranceBranch === b.value}
                onChange={() => set("insuranceBranch", b.value)} className="mt-0.5" />
              <div>
                <div className="font-medium text-sm">{b.label}</div>
                <div className="text-ink-soft text-xs">{b.hint}</div>
              </div>
            </label>
          ))}
          {partBViolation && (
            <p className="text-bad text-xs px-3 mt-1">
              {VACCINE_LABEL[form.vaccineCode]} cannot go Part B (typing tip #12). Switch to Advantage or Tricare.
            </p>
          )}
          {cardViolation && (
            <p className="text-bad text-xs px-3 mt-1">
              Fill in BIN and Cardholder ID from the patient's card before approving.
            </p>
          )}
          {form.insuranceBranch === "fdsmedical" && (
            <Row label="FDSMEDICAL start date">
              <input type="date" value={form.fdsmedicalStartDate ?? ""} onChange={(e) => set("fdsmedicalStartDate", e.target.value || null)} className="input" />
            </Row>
          )}
          {form.insuranceBranch === "railroad" && (
            <Row label="Cardholder ID (RR prefix)">
              <input value={form.railroadCardholderId ?? ""} onChange={(e) => set("railroadCardholderId", e.target.value.toUpperCase() || null)} placeholder="RR..." className="input font-mono" />
            </Row>
          )}
          {(form.insuranceBranch === "partb_card" || form.insuranceBranch === "tricare") && (
            <>
              <p className="text-ink-soft text-xs px-3 pt-1">
                Pharmacy card details from the patient's physical card. If written at the top of the consent form, the AI pre-filled these. Otherwise type them in — bot cannot proceed without them.
              </p>
              <Row label="BIN">
                <input value={form.cardBin ?? ""} maxLength={10} onChange={(e) => set("cardBin", e.target.value || null)} placeholder="e.g., 003858" className="input font-mono w-32" />
              </Row>
              <Row label="PCN">
                <input value={form.cardPcn ?? ""} maxLength={20} onChange={(e) => set("cardPcn", e.target.value.toUpperCase() || null)} className="input font-mono w-40" />
              </Row>
              <Row label="Cardholder ID">
                <input value={form.cardCardholderId ?? ""} maxLength={50} onChange={(e) => set("cardCardholderId", e.target.value || null)} className="input font-mono" />
              </Row>
              <Row label="Group #">
                <input value={form.cardGroupNumber ?? ""} maxLength={50} onChange={(e) => set("cardGroupNumber", e.target.value || null)} className="input font-mono" />
              </Row>
            </>
          )}
        </Section>

        {/* Consent audit */}
        <Section title="Consent form audit">
          <label className="flex items-center gap-2 px-3 py-1.5 text-sm">
            <input type="checkbox" checked={form.consentFormSignaturePresent}
              onChange={(e) => set("consentFormSignaturePresent", e.target.checked)} />
            Patient/caregiver signature present
          </label>
          <Row label="Signed date">
            <input type="date" value={form.consentFormSignedDate ?? ""} onChange={(e) => set("consentFormSignedDate", e.target.value || null)} className="input" />
          </Row>
        </Section>

        {error && <p className="text-bad text-sm px-4 py-2">{error}</p>}

        <div className="sticky bottom-0 bg-surface border-t border-border p-3 flex gap-2 justify-end">
          <button onClick={reject} disabled={saving} className="btn-ghost">Reject</button>
          <button onClick={save}    disabled={saving} className="btn-secondary">{saving ? "Saving…" : "Save"}</button>
          <button onClick={approve} disabled={saving || !canApprove}
            title={!canApprove ? "Fix the highlighted issues before approving" : "Move to PAD queue"}
            className="btn-primary">
            Approve for PioneerRx
          </button>
        </div>
      </div>
    </div>
  );
}

// PATCH template shortcuts in one place so form-side changes stay in sync.
const TEMPLATE_SHORTCUT: Record<VaccineCode, string | null> = {
  fluzonehd: "=fluzonehd", fluad: "=fluad", fluzone: "=fluzone",
  spike: "=spike", pfi: "=pfi", mnex: "=mnex",
  prevnar: null, boostrix: null, shingrix: null, rsv: null,
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-border">
      <h3 className="px-4 py-2 text-xs uppercase tracking-wide text-ink-soft">{title}</h3>
      <div className="pb-2">{children}</div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[130px_1fr] items-center gap-2 px-3 py-1 text-sm">
      <span className="text-ink-soft">{label}</span>
      {children}
    </div>
  );
}

// Normalize whatever the backend sent (ISO datetime, ISO date, or null) to YYYY-MM-DD for <input type="date">.
function toDateInput(v: string | null | undefined): string {
  if (!v) return "";
  return v.slice(0, 10);
}

// Display helper — turn YYYY-MM-DD into MM-DD-YYYY for the age-and-formatted-date caption.
function formatMDY(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[2]}-${m[3]}-${m[1]}` : iso;
}

function ageFrom(iso: string): number | string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return "?";
  const [, y, mo, d] = m;
  const dob = new Date(`${y}-${mo}-${d}T00:00:00Z`);
  const now = new Date();
  let age = now.getUTCFullYear() - dob.getUTCFullYear();
  const beforeBday = now.getUTCMonth() < dob.getUTCMonth()
    || (now.getUTCMonth() === dob.getUTCMonth() && now.getUTCDate() < dob.getUTCDate());
  if (beforeBday) age--;
  return age;
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === "ready_for_pk"  ? "bg-good-soft text-good" :
    status === "tech_review"   ? "bg-warn-soft text-warn" :
    status === "on_hold"       ? "bg-warn-soft text-warn" :
    status === "imported_to_pk"? "bg-good-soft text-good" :
    status === "rejected"      ? "bg-bad-soft text-bad" :
                                 "bg-pill text-ink-soft";
  return <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{status}</span>;
}
